import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Bank as BankIcon,
  Check as CheckIcon,
  Clock as ClockIcon,
  User as UserIcon,
  X as XIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { toast } from 'react-hot-toast';

import type { AmenityReservation } from '@/types/v2/reservation';
import { approveReservation, getPendingReservations, rejectReservation } from '@/api/reservations';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';

export function PendingReservations(): React.JSX.Element {
  const { community } = useUser();
  const queryClient = useQueryClient();
  const rejectDialog = useDialog();

  const [selectedReservation, setSelectedReservation] = React.useState<AmenityReservation | null>(null);
  const [rejectReason, setRejectReason] = React.useState('');
  const [approveDialogOpen, setApproveDialogOpen] = React.useState(false);
  const [reservationToApprove, setReservationToApprove] = React.useState<AmenityReservation | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['pending-reservations', community?.id],
    queryFn: () => getPendingReservations(community?.id || ''),
    enabled: !!community?.id,
  });

  const approveMutation = useMutation({
    mutationFn: ({ reservationId }: { reservationId: string }) =>
      approveReservation(community?.id || '', reservationId, {}),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      toast.success('Reservation approved successfully');
    },
    onError: (error: any) => {
      console.error('Approval error:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to approve reservation';
      toast.error(errorMessage);

      // If the reservation is not found, refresh the list
      if (errorMessage.includes('not found') || errorMessage.includes('null')) {
        queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      }
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ reservationId, reason }: { reservationId: string; reason: string }) =>
      rejectReservation(community?.id || '', reservationId, { rejected_reason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      toast.success('Reservation rejected');
      rejectDialog.handleClose();
      setSelectedReservation(null);
      setRejectReason('');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to reject reservation');
    },
  });

  const handleApprove = (reservation: AmenityReservation) => {
    // Check if reservation is still in pending status
    if (reservation.status !== 'pending') {
      toast.error('This reservation is no longer pending');
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      return;
    }

    setReservationToApprove(reservation);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = () => {
    if (reservationToApprove) {
      approveMutation.mutate({ reservationId: reservationToApprove.id });
    }
    setApproveDialogOpen(false);
    setReservationToApprove(null);
  };

  const handleApproveCancel = () => {
    setApproveDialogOpen(false);
    setReservationToApprove(null);
  };

  const handleRejectClick = (reservation: AmenityReservation) => {
    setSelectedReservation(reservation);
    rejectDialog.handleOpen();
  };

  const handleRejectSubmit = () => {
    if (!selectedReservation) return;
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    rejectMutation.mutate({
      reservationId: selectedReservation.id,
      reason: rejectReason,
    });
  };

  const pendingReservations = data?.pending_reservations || [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (pendingReservations.length === 0) {
    return (
      <Card sx={{ p: 4, textAlign: 'center' }}>
        <BankIcon size={48} weight="duotone" style={{ opacity: 0.5 }} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          No Pending Reservations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All reservations have been processed.
        </Typography>
      </Card>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        {pendingReservations.map((reservation) => (
          <Card key={reservation.id}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'warning.main',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <BankIcon size={24} weight="duotone" color="white" />
                  </Box>
                  <Box>
                    <Typography variant="h6">{reservation.amenity?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Requested {dayjs(reservation.created_at).fromNow()}
                    </Typography>
                  </Box>
                </Box>
                <Chip label="PENDING APPROVAL" size="small" color="warning" sx={{ fontWeight: 600 }} />
              </Stack>

              <Stack spacing={1} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UserIcon size={18} />
                  <Typography variant="body2">
                    {reservation.user?.first_name} {reservation.user?.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({reservation.user?.email})
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ClockIcon size={18} />
                  <Typography variant="body2">
                    {dayjs.utc(reservation.start_time).local().format('MMM D, YYYY h:mm A')} -{' '}
                    {dayjs.utc(reservation.end_time).local().format('h:mm A')}
                  </Typography>
                </Box>

                {reservation.purpose && (
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Purpose:</strong> {reservation.purpose}
                    </Typography>
                  </Box>
                )}

                {reservation.special_requests && (
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Special Requests:</strong> {reservation.special_requests}
                    </Typography>
                  </Box>
                )}

                {reservation.guest_count && (
                  <Typography variant="caption" color="text.secondary">
                    Guests: {reservation.guest_count}
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon size={18} />}
                  onClick={() => handleApprove(reservation)}
                  disabled={approveMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<XIcon size={18} />}
                  onClick={() => handleRejectClick(reservation)}
                  disabled={rejectMutation.isPending}
                >
                  Reject
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Dialog open={rejectDialog.open} onClose={rejectDialog.handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Reservation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this reservation. The resident will be notified.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Reason for Rejection"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g., The amenity is undergoing maintenance during this time..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={rejectDialog.handleClose}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectSubmit}
            disabled={rejectMutation.isPending || !rejectReason.trim()}
          >
            {rejectMutation.isPending ? 'Rejecting...' : 'Reject Reservation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={handleApproveCancel}>
        <DialogTitle>Approve Reservation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve the reservation for{' '}
            <strong>
              {reservationToApprove?.user?.first_name} {reservationToApprove?.user?.last_name}
            </strong>
            ?
          </Typography>
          {reservationToApprove && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                <strong>Date:</strong> {dayjs.utc(reservationToApprove.start_time).local().format('MMM D, YYYY')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Time:</strong> {dayjs.utc(reservationToApprove.start_time).local().format('h:mm A')} - {dayjs.utc(reservationToApprove.end_time).local().format('h:mm A')}
              </Typography>
              {reservationToApprove.amenity?.name && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Amenity:</strong> {reservationToApprove.amenity.name}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleApproveCancel}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveConfirm}
            disabled={approveMutation.isPending}
          >
            {approveMutation.isPending ? 'Approving...' : 'Approve Reservation'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}





